import {
  AmbientLight,
  BoxGeometry,
  BufferGeometry,
  CanvasTexture,
  ColorManagement,
  ConeGeometry,
  CylinderGeometry,
  DoubleSide,
  Group,
  Line,
  LineBasicMaterial,
  LinearSRGBColorSpace,
  Matrix4,
  Mesh,
  MeshPhysicalMaterial,
  OrthographicCamera,
  SpotLight,
  Sprite,
  SpriteMaterial,
  Vector3,
  WebGLRenderer,
} from 'three';
import type { EventDispatcher } from 'three';

import { Stack } from '@frontend/datatypes';

import { getOrCreateThreeColorArray } from '../colors';
import {
  ADDITIONAL_DISPLAY_TYPES,
  ARROW_DEFAULTS,
  COLOR_CODES,
  COLORS,
  MATERIAL_PROPERTIES,
  MM_PER_INCH,
  ORTHOGRAPHIC_CAMERA_DEFAULTS,
  PART_TYPES,
  STOCK_DEFAULTS,
  TRACKBALL_DEFAULTS,
  UNITS,
  PART_VIEWER_MODES,
} from '../constants';
import type { PointJson } from '../contracts/basic';
import type { TessJson } from '../contracts/tess';
import { disposeThreeJS, getMidpoint, isCloseVectors } from '../geom-utils';
import type { Assembly } from '../loaders/assembly';
import { Body } from '../loaders/body';
import { Part } from '../loaders/part';
import type { AnyPart } from '../loaders/part';
import type { Renderer } from '../renderer/renderer';
import { mergeGeometries } from '../utils/BufferGeometryUtils';
import OrthoTrackballControls from './addons/OrthoTrackballControls';
import type Cube from './cube';

export const createRenderer = (rendererInstance: Renderer): WebGLRenderer => {
  if (rendererInstance.disposed) {
    throw new Error('Renderer Instance was disposed already');
  }
  const renderer = new WebGLRenderer({
    alpha: true,
    antialias: true,
    powerPreference: 'high-performance',
  });
  //See https://discourse.threejs.org/t/updates-to-color-management-in-three-js-r152/50791
  //and https://github.com/mrdoob/three.js/wiki/Migration-Guide#151--152
  //r152 introduced default ouputColorSpace of `sRGB`. This broke the look of colors.
  //Also need to opt out of color management
  ColorManagement.enabled = false; //Opting out of color management
  renderer.outputColorSpace = LinearSRGBColorSpace;
  renderer.setPixelRatio(rendererInstance.nodeWindow.devicePixelRatio);
  renderer.setSize(rendererInstance.nodeWidth, rendererInstance.nodeHeight);
  renderer.setClearColor(COLOR_CODES.scene_color, 0);
  renderer.autoClear = false;
  rendererInstance.node.appendChild(renderer.domElement);
  return renderer;
};

export const getSizeAndDivisions = (
  displayUnits: UNITS,
  nativeUnits?: UNITS,
): { size: number; divisions: number } => {
  const nativeIn = nativeUnits === UNITS.IN;
  const displayNative = displayUnits === UNITS.NATIVE;
  const displayIn = displayUnits === UNITS.IN;

  let size = 40;
  let divisions = 40;
  if (displayNative) {
    switch (nativeIn) {
      case true:
        return { size, divisions };
      case false:
        divisions = Math.floor((divisions * MM_PER_INCH) / 10);
        size = Math.floor((size * MM_PER_INCH) / divisions) * divisions;
        return { size, divisions };
    }
  } else {
    switch (displayIn) {
      case true:
        switch (nativeIn) {
          case true:
            return { size, divisions };
          case false:
            divisions = Math.floor((divisions * MM_PER_INCH) / 10);
            size = Math.floor((size * MM_PER_INCH) / divisions) * divisions;
            return { size, divisions };
        }
        break;
      case false:
        switch (nativeIn) {
          case true:
            return { size, divisions };
          case false:
            divisions = Math.floor((divisions * MM_PER_INCH) / 10);
            size = Math.floor((size * MM_PER_INCH) / divisions) * divisions;
            return { size, divisions };
        }
    }
  }
};

export const createOrthographicCamera = (
  renderer: Renderer,
): OrthographicCamera => {
  const camera = new OrthographicCamera(
    renderer.nodeWidth / -2,
    renderer.nodeWidth / 2,
    renderer.nodeHeight / 2,
    renderer.nodeHeight / -2,
    0.1,
    ORTHOGRAPHIC_CAMERA_DEFAULTS.FAR,
  );
  camera.up = new Vector3(0, 0, 1);
  renderer.scene.add(camera);
  return camera;
};

export const createSpotLight = (renderer: Renderer | Cube): SpotLight => {
  // https://stackoverflow.com/questions/16456912/point-spotlight-in-same-direction-as-camera-three-js-flashlight
  const spotLight = new SpotLight(COLOR_CODES.spotlight, 0.5 * Math.PI);
  spotLight.up = renderer.camera.up;
  // add the spotlight to the camera and have the spotlight target be the camera
  renderer.camera.add(spotLight);
  spotLight.decay = 0.0;
  spotLight.position.set(0, 0, 1);
  spotLight.target = renderer.camera;
  spotLight.angle = Math.PI / 2;
  return spotLight;
};

export const createLight = (renderer: Renderer): AmbientLight => {
  const light = new AmbientLight(COLORS.soft_white, 0.5 * Math.PI);
  renderer.scene.add(light);
  renderer.scene2.add(new AmbientLight(COLORS.soft_white, 0.5 * Math.PI));
  return light;
};

export type OrthTrackballControlsType = EventDispatcher & {
  bottom0: number;
  dynamicDampingFactor: number;
  enabled: boolean;
  handleResize: () => void;
  left0: number;
  noRotate: boolean;
  noZoom: boolean;
  position0: Vector3;
  right0: number;
  rotateSpeed: number;
  staticMoving: boolean;
  target0: Vector3;
  target: Vector3;
  top0: number;
  up0: Vector3;
  update(): void;
  zoomSpeed: number;
  dispose(): void;
};

export const createOrthoTrackball = (
  renderer: Renderer,
): OrthTrackballControlsType => {
  const trackball = new OrthoTrackballControls(
    renderer.camera,
    renderer.renderer.domElement,
    renderer.state.onCameraZoomChange,
  ) as unknown as OrthTrackballControlsType;

  // noZoom because we manage zoom internally with zoom on cursor
  trackball.noZoom = true;
  trackball.noRotate =
    renderer.state.viewerMode === PART_VIEWER_MODES.VECTOR_EDIT;
  trackball.rotateSpeed = TRACKBALL_DEFAULTS.ROTATE_SPEED;
  trackball.zoomSpeed = TRACKBALL_DEFAULTS.ZOOM_SPEED;
  trackball.staticMoving = TRACKBALL_DEFAULTS.STATIC_MOVING;
  trackball.dynamicDampingFactor = TRACKBALL_DEFAULTS.DYNAMIC_DAMPING_FACTOR;
  return trackball;
};

const createArrow = (
  color: number,
  vector: PointJson,
  initialPosition: Vector3,
  arrowOffset: number,
): Mesh<BufferGeometry, MeshPhysicalMaterial> => {
  // set arrow dimensions based on inputted offset
  const cylRadius = ARROW_DEFAULTS.CYLINDER_RADIUS * arrowOffset;
  const cylHeight = ARROW_DEFAULTS.CYLINDER_HEIGHT * arrowOffset;
  const coneRadius = ARROW_DEFAULTS.CONE_RADIUS * arrowOffset;
  const coneHeight = ARROW_DEFAULTS.CONE_HEIGHT * arrowOffset;

  const dir = vector;
  const transVec = new Vector3(dir[0], dir[1], dir[2]);
  const rotVec = new Vector3(dir[0] * -1, dir[1] * -1, dir[2] * -1);

  //Geometry is merged cone and cylinder
  //* cone is slid up so that it is at the tip of the cylinder
  const coneGeometry = new ConeGeometry(coneRadius, coneHeight, 64);
  coneGeometry.computeVertexNormals(); //Make the faces look smoother
  const placedConeGeometry = coneGeometry.translate(
    0,
    cylHeight / 2 + coneHeight / 2,
    0,
  );
  const cylinderGeometry = new CylinderGeometry(
    cylRadius,
    cylRadius,
    cylHeight,
    64,
  );
  cylinderGeometry.normalizeNormals();
  cylinderGeometry.computeVertexNormals(); //Make the faces look smoother

  const arrowGeometry = mergeGeometries([placedConeGeometry, cylinderGeometry]);
  arrowGeometry.normalizeNormals();
  arrowGeometry.computeVertexNormals(); //make the faces look smoother

  const arr = new Mesh<BufferGeometry, MeshPhysicalMaterial>(
    arrowGeometry,
    new MeshPhysicalMaterial({
      color,
      reflectivity: 0.1,
      metalness: 0.1,
    }),
  );
  // now slide it forward so that it is sitting at the origin pointing in +y
  arr.geometry.translate(0, cylHeight / 2, 0);

  // now translate it to the desired position and then rotate accordingly via quaternion
  // https://stackoverflow.com/questions/28508194/three-js-move-object-along-face-normal-direction
  // position this relative to the bounding box length
  const p1 = new Vector3(0, 0, 0);
  p1.addVectors(
    initialPosition,
    transVec.clone().multiplyScalar(cylHeight + coneHeight),
  );
  arr.position.addVectors(p1, transVec.multiplyScalar(arrowOffset));

  // now rotate it to point anti parallel to the direction vector
  // establish the current direction of the system (positive y)
  // https://stackoverflow.com/questions/9038465/three-js-object3d-cylinder-rotation-to-align-to-a-vector
  const axis = new Vector3(0, 1, 0);
  arr.quaternion.setFromUnitVectors(axis, rotVec.clone().normalize());

  arr.visible = true;
  return arr;
};

type WireContent = {
  p1: PointJson;
  p2: PointJson;
  intersecting?: boolean;
};

type LineContent = [PointJson, PointJson];

export type AdditionalViewerContent = {
  /*eslint-disable camelcase*/
  axial?: PointJson;
  bad_shape?: TessJson;
  body?: unknown;
  center?: PointJson;
  check_wires?: WireContent[];
  counter_body?: TessJson;
  direction?: PointJson;
  height_vector?: PointJson;
  length_vector?: PointJson;
  lines?: ArrayLike<LineContent> | Record<string, LineContent>;
  max_height?: number;
  max_length?: number;
  max_radius?: number;
  max_width?: number;
  partial_cut?: TessJson;
  partial_cuts?: TessJson[];
  pointer?: PointJson;
  root_body?: TessJson;
  stock_length?: number;
  stock_radius?: number;
  vector?: PointJson;
  width_vector?: PointJson;
  /*eslint-enable camelcase*/
};

export type FeatureOrFeedBackItem = {
  referenceUuid: string;
  key: string;
  name: string;
  isLegacy?: boolean;
  additionalViewerContent: AdditionalViewerContent;
  additionalViewerContentInfo: Record<
    keyof AdditionalViewerContent,
    ADDITIONAL_DISPLAY_TYPES
  >;
  color: number;
  transform?: number[];
};

const feedbackMaterial = new MeshPhysicalMaterial({
  color: STOCK_DEFAULTS.color,
  transparent: true,
  opacity: STOCK_DEFAULTS.opacity,
  reflectivity: 0.1,
  metalness: 0.1,
  side: DoubleSide,
});

const legacyMakeAdditionalFeedbackForFeature = (
  f: FeatureOrFeedBackItem,
  partOrAssembly: AnyPart | Assembly,
  renderer: Renderer,
): Group | null => {
  const needsAdditional =
    f.name === 'direction' ||
    f.name === 'machine_direction' ||
    f.name === 'wire_edm_setup' ||
    f.name === 'sheet_metal_flat_part' ||
    f.name === 'lathe_setup' ||
    f.name === 'lathe_attack' ||
    f.name === 'live_tooling_radial_direction' ||
    f.name === 'live_tooling_axial_direction' ||
    f.name === 'ignored_body_in_shape' ||
    'lines' in f.additionalViewerContent ||
    'body' in f.additionalViewerContent ||
    'partial_cut' in f.additionalViewerContent ||
    'stock_radius' in f.additionalViewerContent ||
    'check_wires' in f.additionalViewerContent ||
    'length_vector' in f.additionalViewerContent ||
    'max_radius' in f.additionalViewerContent;

  if (needsAdditional) {
    const additionalGroup = new Group();
    additionalGroup.name = f.key;
    additionalGroup.visible = false;
    if ('lines' in f.additionalViewerContent) {
      const mat = new LineBasicMaterial({
        color: COLOR_CODES.additionalLine,
        linewidth: MATERIAL_PROPERTIES.ADDITIONAL_FEEDBACK_LINEWIDTH,
      });
      const lines = f.additionalViewerContent.lines || [];
      for (const arr of Object.values(lines)) {
        const geo = new BufferGeometry().setFromPoints([
          new Vector3(arr[0][0], arr[0][1], arr[0][2]),
          new Vector3(arr[1][0], arr[1][1], arr[1][2]),
        ]);
        const line = new Line(geo, mat);
        additionalGroup.add(line);
      }
    } else if ('root_body' in f.additionalViewerContent) {
      // for sheet metal
      if (f.additionalViewerContent.root_body) {
        const flattenedRootPart = new Part<PART_TYPES.STEP>(
          {
            tess: f.additionalViewerContent.root_body,
          },
          renderer,
        );
        flattenedRootPart.load();
        const flattenedRoot = new Body({}, flattenedRootPart, renderer);
        flattenedRoot.load();
        additionalGroup.add(flattenedRoot.group);
      }
      if (f.additionalViewerContent.counter_body) {
        const flattenedCounterPart = new Part<PART_TYPES.STEP>(
          {
            tess: f.additionalViewerContent.counter_body,
          },
          renderer,
        );
        flattenedCounterPart.load();
        const flattenedCounter = new Body({}, flattenedCounterPart, renderer);
        flattenedCounter.load();
        additionalGroup.add(flattenedCounter.group);
      }
    } else if (
      f.name === 'direction' ||
      f.name === 'machine_direction' ||
      f.name === 'wire_edm_setup' ||
      f.name === 'lathe_setup' ||
      f.name === 'lathe_attack' ||
      f.name === 'live_tooling_axial_direction' ||
      f.name === 'live_tooling_radial_direction'
    ) {
      const part = partOrAssembly as AnyPart;
      if (!part.isLoaded) {
        throw new Error(
          'Cannot create arrow additional feedback when part geometry is not loaded',
        );
      }
      part.setBoxBounds();
      const arrow = createArrow(
        f.color,
        f.additionalViewerContent.vector ||
          (f.additionalViewerContent.pointer as PointJson),
        getMidpoint(part.boxMin, part.boxMax),
        new Vector3().subVectors(part.boxMax, part.boxMin).length() / 2,
      );
      additionalGroup.add(arrow);
      if ('axial' in f.additionalViewerContent) {
        const arrow = createArrow(
          f.color,
          f.additionalViewerContent.axial as PointJson,
          getMidpoint(part.boxMin, part.boxMax),
          new Vector3().subVectors(part.boxMax, part.boxMin).length() / 2,
        );
        additionalGroup.add(arrow);
      }
      if (f.additionalViewerContent.partial_cuts) {
        for (const cut of f.additionalViewerContent.partial_cuts) {
          //TODO: Is this the best way to create the geometry? We are creating A LOT of parts and bodies here for a single face per cut
          //Since all cuts are in the same group and we always toggle all faces on/off as a group, why not make them the same body?
          //And we don't keep a reference to the Part or Body created here...
          const partialFacePart = new Part<PART_TYPES.STEP>(
            {
              tess: {
                ...cut,
                Type: PART_TYPES.STEP,
              },
            },
            renderer,
          );
          partialFacePart.load();
          const partialFace = new Body({}, partialFacePart, renderer);
          partialFace.load();
          const [firstFace] = partialFace.obj.faces.values();
          firstFace.setColorAndOpacity([
            ...getOrCreateThreeColorArray(f.color),
            0.5,
          ]);
          //Normally a body's renderable is false. And the visibility of faces is adjusted manually by paint-utils
          //But for feedback bodies, the visibility is set using the group because currently we only track groups in a part, assembly, body and assemblyInstance's
          //additionalFeedback. So the firstFace must have visible true because we always want to turn the visibility of the full group on/off and don't have direct
          //access to the renderable.
          firstFace.visible = true;
          additionalGroup.add(partialFace.group);
        }
      }
    } else if ('partial_cut' in f.additionalViewerContent) {
      //TODO: Is this the best way to create the geometry?
      //We don't keep a reference to the Part or Body created here...
      const partialFacePart = new Part<PART_TYPES.STEP>(
        {
          tess: {
            ...(f.additionalViewerContent.partial_cut as TessJson),
            Type: PART_TYPES.STEP,
          },
        },
        renderer,
      );
      partialFacePart.load();
      const partialFace = new Body({}, partialFacePart, renderer);
      partialFace.load();
      const [firstFace] = partialFace.obj.faces.values();
      firstFace.setColorAndOpacity([
        ...getOrCreateThreeColorArray(COLORS.red),
        0.5,
      ]);
      //Normally a body's renderable is false. And the visibility of faces is adjusted manually by paint-utils
      //But for feedback bodies, the visibility is set using the group because currently we only track groups in a part, assembly, body and assemblyInstance's
      //additionalFeedback. So the firstFace must have visible true because we always want to turn the visibility of the full group on/off and don't have direct
      //access to the renderable.
      firstFace.visible = true;
      additionalGroup.add(partialFace.group);
    } else if (
      'stock_radius' in f.additionalViewerContent ||
      'max_radius' in f.additionalViewerContent
    ) {
      if (
        f.additionalViewerContent.stock_radius &&
        f.additionalViewerContent.stock_length &&
        f.additionalViewerContent.center &&
        f.additionalViewerContent.direction
      ) {
        const { stock_length: stockLength, stock_radius: stockRadius } =
          f.additionalViewerContent;
        const cylinder = new Mesh(
          new CylinderGeometry(stockRadius, stockRadius, stockLength, 50),
          feedbackMaterial,
        );
        const center = new Vector3(
          f.additionalViewerContent.center[0],
          f.additionalViewerContent.center[1],
          f.additionalViewerContent.center[2],
        );
        const direction = new Vector3(
          f.additionalViewerContent.direction[0],
          f.additionalViewerContent.direction[1],
          f.additionalViewerContent.direction[2],
        );
        // the default native up for environment is +y
        const up = new Vector3(0, 1, 0);
        if (!isCloseVectors(up, direction)) {
          const rotAxis = new Vector3().crossVectors(direction, up).normalize();
          const angle = up.angleTo(direction);
          cylinder.rotateOnAxis(rotAxis, -angle);
          cylinder.position.set(center.x, center.y, center.z);
          cylinder.translateOnAxis(up, stockLength / 2);
        } else {
          cylinder.position.set(center.x, center.y, center.z);
          cylinder.translateOnAxis(up, stockLength / 2);
        }
        cylinder.renderOrder = 10000;
        additionalGroup.add(cylinder);
      } else if (
        f.additionalViewerContent.max_radius &&
        f.additionalViewerContent.max_length &&
        f.additionalViewerContent.center &&
        f.additionalViewerContent.direction
      ) {
        const { max_radius: maxRadius, max_length: maxLength } =
          f.additionalViewerContent;
        const cylinder = new Mesh(
          new CylinderGeometry(maxRadius, maxRadius, maxLength, 50),
          feedbackMaterial,
        );
        const center = new Vector3(
          f.additionalViewerContent.center[0],
          f.additionalViewerContent.center[1],
          f.additionalViewerContent.center[2],
        );
        const direction = new Vector3(
          f.additionalViewerContent.direction[0],
          f.additionalViewerContent.direction[1],
          f.additionalViewerContent.direction[2],
        );
        // the default native up for environment is +y
        const up = new Vector3(0, 1, 0);
        if (!isCloseVectors(up, direction)) {
          const rotAxis = new Vector3().crossVectors(direction, up).normalize();
          const angle = up.angleTo(direction);
          cylinder.rotateOnAxis(rotAxis, -angle);
          cylinder.position.set(center.x, center.y, center.z);
          cylinder.translateOnAxis(up, maxLength / 2);
        } else {
          cylinder.position.set(center.x, center.y, center.z);
          cylinder.translateOnAxis(up, maxLength / 2);
        }
        cylinder.renderOrder = 10000;
        additionalGroup.add(cylinder);
      }
    } else if ('length_vector' in f.additionalViewerContent) {
      if (
        f.additionalViewerContent.length_vector &&
        f.additionalViewerContent.width_vector &&
        f.additionalViewerContent.height_vector &&
        f.additionalViewerContent.max_length &&
        f.additionalViewerContent.max_width &&
        f.additionalViewerContent.max_height &&
        f.additionalViewerContent.center
      ) {
        const center = new Vector3(
          f.additionalViewerContent.center[0],
          f.additionalViewerContent.center[1],
          f.additionalViewerContent.center[2],
        );
        const lengthDirection = new Vector3(
          f.additionalViewerContent.length_vector[0],
          f.additionalViewerContent.length_vector[1],
          f.additionalViewerContent.length_vector[2],
        );
        const widthDirection = new Vector3(
          f.additionalViewerContent.width_vector[0],
          f.additionalViewerContent.width_vector[1],
          f.additionalViewerContent.width_vector[2],
        );
        const heightDirection = new Vector3(
          f.additionalViewerContent.height_vector[0],
          f.additionalViewerContent.height_vector[1],
          f.additionalViewerContent.height_vector[2],
        );
        const baseTransform = new Matrix4().makeBasis(
          widthDirection,
          heightDirection,
          lengthDirection,
        );
        const positionSet = new Vector3().addVectors(
          center,
          new Vector3()
            .copy(heightDirection)
            .multiplyScalar(f.additionalViewerContent.max_height / 2),
        );
        baseTransform.setPosition(positionSet);
        const box = new Mesh(
          new BoxGeometry(
            f.additionalViewerContent.max_width,
            f.additionalViewerContent.max_height,
            f.additionalViewerContent.max_length,
          ),
          feedbackMaterial,
        );
        box.applyMatrix4(baseTransform);

        box.renderOrder = 10000;
        additionalGroup.add(box);
      }
    } else if ('check_wires' in f.additionalViewerContent) {
      const checkWires = f.additionalViewerContent.check_wires as {
        p1: PointJson;
        p2: PointJson;
        intersecting?: boolean;
      }[];
      for (const wire of checkWires) {
        const p1 = new Vector3(wire.p1[0], wire.p1[1], wire.p1[2]);
        const p2 = new Vector3(wire.p2[0], wire.p2[1], wire.p2[2]);
        const geo = new BufferGeometry().setFromPoints([p1, p2]);
        const mat = new LineBasicMaterial({
          color: wire.intersecting ? COLORS.red : COLORS.black,
          linewidth: MATERIAL_PROPERTIES.LINEWIDTH,
        });
        const line = new Line(geo, mat);
        additionalGroup.add(line);
      }
    } else if ('bad_shape' in f.additionalViewerContent) {
      const badShapePart = new Part<PART_TYPES.STEP>(
        {
          tess: {
            ...(f.additionalViewerContent.bad_shape as TessJson),
            Type: PART_TYPES.STEP,
          },
        },
        renderer,
      );
      badShapePart.load();
      const badShapeBody = new Body({}, badShapePart, renderer);
      badShapeBody.load();
      additionalGroup.add(badShapeBody.group);
    }

    return additionalGroup;
  } else {
    return null;
  }
};

const makeArrowAdditionalFeedback = <T extends PART_TYPES>(
  part: Part<T>,
  vectorContent: PointJson,
  label: number,
): Mesh<BufferGeometry, MeshPhysicalMaterial> => {
  if (!part.isLoaded) {
    throw new Error(
      'Cannot create arrow additional feedback when part geometry is not loaded',
    );
  }
  part.setBoxBounds();
  return createArrow(
    label,
    vectorContent,
    getMidpoint(part.boxMin, part.boxMax),
    new Vector3().subVectors(part.boxMax, part.boxMin).length() / 2,
  );
};

type BoxContent = {
  length: number;
  height: number;
  width: number;
  center: PointJson;
  /*eslint-disable camelcase*/
  length_vector: PointJson;
  width_vector: PointJson;
  height_vector: PointJson;
  /*eslint-enable camelcase*/
};

const makeBoxAdditionalFeedback = ({
  length,
  height,
  width,
  center: rawCenter,
  length_vector: [lengthVector0, lengthVector1, lengthVector2],
  width_vector: [widthVector0, widthVector1, widthVector2],
  height_vector: [heightVector0, heightVector1, heightVector2],
}: BoxContent): Mesh<BoxGeometry, MeshPhysicalMaterial> => {
  const center = new Vector3(rawCenter[0], rawCenter[1], rawCenter[2]);
  const lengthDirection = new Vector3(
    lengthVector0,
    lengthVector1,
    lengthVector2,
  );
  const widthDirection = new Vector3(widthVector0, widthVector1, widthVector2);
  const heightDirection = new Vector3(
    heightVector0,
    heightVector1,
    heightVector2,
  );
  const baseTransform = new Matrix4().makeBasis(
    widthDirection,
    heightDirection,
    lengthDirection,
  );
  const positionSet = new Vector3().addVectors(
    center,
    new Vector3().copy(heightDirection).multiplyScalar(height / 2),
  );
  baseTransform.setPosition(positionSet);
  const box = new Mesh<BoxGeometry, MeshPhysicalMaterial>(
    new BoxGeometry(width, height, length),
    feedbackMaterial,
  );
  box.applyMatrix4(baseTransform);

  box.renderOrder = 10000;
  return box;
};

const makeCheckWiresAdditionalFeedback = (
  content: WireContent[],
): Line<BufferGeometry, LineBasicMaterial>[] => {
  const lines: Line<BufferGeometry, LineBasicMaterial>[] = [];
  for (const wire of content) {
    const p1 = new Vector3(wire.p1[0], wire.p1[1], wire.p1[2]);
    const p2 = new Vector3(wire.p2[0], wire.p2[1], wire.p2[2]);
    const geo = new BufferGeometry().setFromPoints([p1, p2]);
    const mat = new LineBasicMaterial({
      color: wire.intersecting ? COLORS.red : COLORS.black,
      linewidth: MATERIAL_PROPERTIES.LINEWIDTH,
    });
    const line = new Line<BufferGeometry, LineBasicMaterial>(geo, mat);
    lines.push(line);
  }
  return lines;
};

type CylinderContent = {
  center: PointJson;
  direction: PointJson;
  length: number;
  radius: number;
};

const makeCylinderAdditionalFeedback = ({
  center: rawCenter,
  direction: rawDirection,
  length,
  radius,
}: CylinderContent): Mesh<CylinderGeometry, MeshPhysicalMaterial> => {
  const cylinder = new Mesh<CylinderGeometry, MeshPhysicalMaterial>(
    new CylinderGeometry(radius, radius, length, 50),
    feedbackMaterial,
  );
  const center = new Vector3(rawCenter[0], rawCenter[1], rawCenter[2]);
  const direction = new Vector3(
    rawDirection[0],
    rawDirection[1],
    rawDirection[2],
  );
  // the default native up for environment is +y
  const up = new Vector3(0, 1, 0);
  if (!isCloseVectors(up, direction)) {
    const rotAxis = new Vector3().crossVectors(direction, up).normalize();
    const angle = up.angleTo(direction);
    cylinder.rotateOnAxis(rotAxis, -angle);
    cylinder.position.set(center.x, center.y, center.z);
    cylinder.translateOnAxis(up, length / 2);
  } else {
    cylinder.position.set(center.x, center.y, center.z);
    cylinder.translateOnAxis(up, length / 2);
  }
  cylinder.renderOrder = 10000;

  return cylinder;
};

const makePartialFaceAdditionalFeedback = (
  tess: TessJson,
  renderer: Renderer,
): Group => {
  //TODO: Is this the best way to create the geometry?
  //We don't keep a reference to the Part or Body created here...
  const partialFacePart = new Part<PART_TYPES.STEP>(
    {
      tess: {
        ...tess,
        Type: PART_TYPES.STEP,
      },
    },
    renderer,
  );
  partialFacePart.load();
  const partialFace = new Body<PART_TYPES.STEP>({}, partialFacePart, renderer);
  partialFace.load();
  const [firstFace] = partialFace.obj.faces.values();
  firstFace.setColorAndOpacity([
    ...getOrCreateThreeColorArray(COLORS.red),
    0.5,
  ]);
  //Normally a body's renderable is false. And the visibility of faces is adjusted manually by paint-utils
  //But for feedback bodies, the visibility is set using the group because currently we only track groups in a part, assembly, body and assemblyInstance's
  //additionalFeedback. So the firstFace must have visible true because we always want to turn the visibility of the full group on/off and don't have direct
  //access to the renderable.
  firstFace.visible = true;
  return partialFace.group;
};

const makePartialFaceArrayAdditionalFeedback = (
  cuts: TessJson[],
  color: number,
  renderer: Renderer,
): Group[] => {
  //TODO: Is this the best way to create the geometry? We are creating A LOT of parts and bodies here for a single face per cut
  //Later these groups are consolidated under another group, which we always turn visibility on/off for.  So do we need this extra level of hiearchy
  //And we don't keep a reference to the Part or Body created here...
  const groups = [];
  for (const cut of cuts) {
    const partialFacePart = new Part<PART_TYPES.STEP>(
      {
        tess: {
          ...cut,
          Type: PART_TYPES.STEP,
        },
      },
      renderer,
    );
    partialFacePart.load();
    const partialFace = new Body<PART_TYPES.STEP>(
      {},
      partialFacePart,
      renderer,
    );
    partialFace.load();
    const [firstFace] = partialFace.obj.faces.values();
    firstFace.setColorAndOpacity([...getOrCreateThreeColorArray(color), 0.5]);
    //Normally a body's renderable is false. And the visibility of faces is adjusted manually by paint-utils
    //But for feedback bodies, the visibility is set using the group because currently we only track groups in a part, assembly, body and assemblyInstance's
    //additionalFeedback. So the firstFace must have visible true because we always want to turn the visibility of the full group on/off and don't have direct
    //access to the renderable.
    firstFace.visible = true;
    groups.push(partialFace.group);
  }
  return groups;
};

const makeLinesAdditionalFeedback = (content: LineContent[]): Line[] => {
  const mat = new LineBasicMaterial({
    color: COLOR_CODES.additionalLine,
    linewidth: MATERIAL_PROPERTIES.ADDITIONAL_FEEDBACK_LINEWIDTH,
  });
  const lines = [];
  for (const arr of content) {
    const geo = new BufferGeometry().setFromPoints([
      new Vector3(arr[0][0], arr[0][1], arr[0][2]),
      new Vector3(arr[1][0], arr[1][1], arr[1][2]),
    ]);
    const line = new Line(geo, mat);
    lines.push(line);
  }
  return lines;
};

type CurvilinearEdgeContent = {
  Edges: { points: PointJson[] }[];
};

const makeCurvilinearEdgesAdditionalFeedback = (
  content: CurvilinearEdgeContent,
): Line[] => {
  const mat = new LineBasicMaterial({
    color: COLOR_CODES.additionalLine,
    linewidth: MATERIAL_PROPERTIES.ADDITIONAL_FEEDBACK_LINEWIDTH,
  });
  const lines = [];
  for (const edge of content.Edges) {
    const geo = new BufferGeometry().setFromPoints(
      edge.points.map((point) => new Vector3(...point)),
    );
    const line = new Line(geo, mat);
    lines.push(line);
  }
  return lines;
};

const makeTessellationAdditionalFeedback = (
  tess: TessJson,
  renderer: Renderer,
): Group => {
  const part = new Part<PART_TYPES.STEP>(
    {
      tess: {
        ...tess,
        Type: PART_TYPES.STEP,
      },
    },
    renderer,
  );
  part.load();
  const body = new Body<PART_TYPES.STEP>({}, part, renderer);
  body.load();
  return body.group;
};

const makeFeatureVolumeAdditionalFeedback = (
  tess: TessJson,
  color: string | number,
  renderer: Renderer,
): Group => {
  const part = new Part<PART_TYPES.STEP>(
    {
      tess: {
        ...tess,
        Type: PART_TYPES.STEP,
      },
    },
    renderer,
  );
  part.load();
  const body = new Body({ color }, part, renderer);
  body.load();
  return body.group;
};

const makeAdditionalFeedbackForFeature = (
  feature: FeatureOrFeedBackItem,
  partOrAssembly: AnyPart | Assembly,
  renderer: Renderer,
): Group | null => {
  const { additionalViewerContent, additionalViewerContentInfo, color } =
    feature;
  const additionalGroup = new Group();
  additionalGroup.visible = false;
  for (const contentKey in additionalViewerContentInfo) {
    const additionalDisplayType: ADDITIONAL_DISPLAY_TYPES =
      additionalViewerContentInfo[contentKey as keyof AdditionalViewerContent];
    const content = (additionalViewerContent || {})[
      contentKey as keyof AdditionalViewerContent
    ];
    if (content) {
      switch (additionalDisplayType) {
        case ADDITIONAL_DISPLAY_TYPES.ARROW:
          additionalGroup.add(
            makeArrowAdditionalFeedback(
              partOrAssembly as AnyPart,
              content as PointJson,
              color,
            ),
          );
          break;
        case ADDITIONAL_DISPLAY_TYPES.BOX:
          additionalGroup.add(makeBoxAdditionalFeedback(content as BoxContent));
          break;
        case ADDITIONAL_DISPLAY_TYPES.CHECK_WIRES: {
          const groups = makeCheckWiresAdditionalFeedback(
            content as WireContent[],
          );
          if (groups.length) {
            additionalGroup.add(...groups);
          }
          break;
        }
        case ADDITIONAL_DISPLAY_TYPES.CYLINDER:
          additionalGroup.add(
            makeCylinderAdditionalFeedback(content as CylinderContent),
          );
          break;
        case ADDITIONAL_DISPLAY_TYPES.LINES: {
          const groups = makeLinesAdditionalFeedback(content as LineContent[]);
          if (groups.length) {
            additionalGroup.add(...groups);
          }
          break;
        }
        case ADDITIONAL_DISPLAY_TYPES.CURVILINEAR_EDGES: {
          const groups = makeCurvilinearEdgesAdditionalFeedback(
            content as CurvilinearEdgeContent,
          );
          if (groups.length) {
            additionalGroup.add(...groups);
          }
          break;
        }
        case ADDITIONAL_DISPLAY_TYPES.PARTIAL_FACE:
          additionalGroup.add(
            makePartialFaceAdditionalFeedback(content as TessJson, renderer),
          );
          break;
        case ADDITIONAL_DISPLAY_TYPES.PARTIAL_FACE_ARRAY: {
          const groups = makePartialFaceArrayAdditionalFeedback(
            content as TessJson[],
            color,
            renderer,
          );
          if (groups.length) {
            additionalGroup.add(...groups);
          }
          break;
        }
        case ADDITIONAL_DISPLAY_TYPES.TESSELLATION:
          additionalGroup.add(
            makeTessellationAdditionalFeedback(content as TessJson, renderer),
          );
          if (feature.transform) {
            const transform = new Matrix4().fromArray(feature.transform);
            additionalGroup.applyMatrix4(transform);
          }
          break;
        case ADDITIONAL_DISPLAY_TYPES.FEATURE_VOLUME_DISPLAY:
          additionalGroup.add(
            makeFeatureVolumeAdditionalFeedback(
              content as TessJson,
              color,
              renderer,
            ),
          );
          break;
      }
    }
  }
  if (additionalGroup.children.length > 0) {
    return additionalGroup;
  } else {
    return null;
  }
};

export const createAdditionalFeedback = <T extends PART_TYPES>(
  partOrAssembly: Part<T> | Assembly,
  featuresOrFeedbackItems: Iterable<FeatureOrFeedBackItem>,
  renderer: Renderer,
): boolean => {
  const additionalFeedback = partOrAssembly.additionalFeedback;
  let changed = false;
  for (const featureOrFeedbackItem of featuresOrFeedbackItems) {
    const key = featureOrFeedbackItem.key;
    if (additionalFeedback[key]) {
      continue; //already known feature or feedbackItem
    }
    const additionalGroup = featureOrFeedbackItem.isLegacy
      ? legacyMakeAdditionalFeedbackForFeature(
          featureOrFeedbackItem,
          partOrAssembly,
          renderer,
        )
      : makeAdditionalFeedbackForFeature(
          featureOrFeedbackItem,
          partOrAssembly,
          renderer,
        );
    if (additionalGroup) {
      additionalGroup.name = `id|${partOrAssembly.id}|additionalFeedback|${key}`;
      additionalFeedback[key] = additionalGroup;

      //Turn the visibility of the additionalFeedback[key] off (by default) and turn its descendents visibility on
      //The visibility of this additional feedback for the key will be controlled by toggling the visibility of the top level group
      additionalGroup.visible = false;
      const stack = Stack.from(additionalGroup.children);
      let item;
      //eslint-disable-next-line no-cond-assign
      while ((item = stack.pop())) {
        item.visible = true;
        for (const child of item.children) {
          stack.push(child);
        }
      }

      changed = true;
    }
  }
  return changed;
};

//Similar to disposeThreeJS in geom-utils.ts
export const disposeAdditionalFeedback = (
  additionalFeedback: Record<string, Group>,
) => {
  for (const [key, g] of Object.entries(additionalFeedback)) {
    disposeThreeJS(g);
    //Note: Doesn't dispose the dummy parts/assemblies/bodies/assemblyInstances created
    delete additionalFeedback[key];
  }
};

export const createSignature = (): Sprite => {
  const message = 'Geometry Drives Everything';
  const submessage = '--Euclid I--';
  const canvas = document.createElement('canvas');
  canvas.width = 1024;
  canvas.height = 1024;
  const context = canvas.getContext('2d') as CanvasRenderingContext2D;
  const fontsize = 50;
  context.font = `${fontsize}px Times New Roman`;
  const metrics = context.measureText(message);
  context.fillStyle = '#ed6c24';
  context.fillText(
    message,
    (canvas.width - metrics.width) / 2,
    canvas.height - (canvas.height - fontsize) / 2,
  );
  const subMetrics = context.measureText(submessage);
  context.fillText(
    submessage,
    (canvas.width - subMetrics.width) / 2,
    canvas.height - (canvas.height - fontsize) / 2 + fontsize,
  );

  const texture = new CanvasTexture(canvas);
  const spriteMaterial = new SpriteMaterial({ map: texture });
  // this makes it visible from everywhere
  spriteMaterial.depthTest = false;
  const sprite = new Sprite(spriteMaterial);
  sprite.scale.set(100, 100, 1);
  sprite.visible = false;
  return sprite;
};
